import { useCallback, useMemo, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { ChevronDown, Folder, X } from "lucide-react";
import {
  Card,
  BlockStack,
  Text,
  ButtonGroup,
  TextField,
} from "@shopify/polaris";
import UploadFile from "features/upload-file/upload-file";
import FavoriteCategoryFilter from "components/favorite-category-filter/favorite-category-filter";
import Files from "features/files/files";
import CustomerDocumentTree from "features/customer-documents/customer-document-tree";
import useCustomerDocumentTree from "features/customer-documents/use-customer-document-tree";
import { ALL_NODE_ID } from "features/customer-documents/build-document-tree";
import { isSelectableRow } from "features/files/file-row-model";
import useAddFilesToCart from "features/document-cart/use-add-files-to-cart";

// Placeholder rows that mirror the file list (thumbnail + title/subtitle + the
// two row action buttons), shown while files load instead of a bare spinner.
const FilesListSkeleton = () => (
  <div
    className="animate-pulse flex flex-col"
    aria-hidden="true"
    data-testid="files-list-skeleton"
  >
    {Array.from({ length: 5 }).map((_, i) => (
      <div
        key={i}
        className="flex items-center gap-3 py-3 border-b border-[#F2F1EF] last:border-b-0"
      >
        <div className="h-9 w-9 rounded bg-gray-200" />
        <div className="flex-grow">
          <div className="h-3 w-1/2 rounded bg-gray-200 mb-2" />
          <div className="h-3 w-1/4 rounded bg-gray-100" />
        </div>
        <div className="h-7 w-7 rounded bg-gray-200" />
        <div className="h-7 w-7 rounded bg-gray-200" />
      </div>
    ))}
  </div>
);

const CustomerDocumentsSection = ({
  t,
  companyId,
  customerId,
  searchQuery,
  handleSearchChange,
  selectedCategoryFilters,
  handleCategoryFilterChange,
  isLoadingCustomerFiles,
  isLoadingFilteredFiles,
  filteredFiles,
  handleOnFileChange,
}) => {
  // The flat list of everything filed against the customer is browsed through
  // the same category tree as the Documents page, pruned to the branches this
  // customer actually has documents in.
  const { tree, selectedId, selectedTitle, selectCategory, visibleFiles } =
    useCustomerDocumentTree({ companyId, files: filteredFiles });
  const selectedBranchTitle =
    selectedId === ALL_NODE_ID
      ? t("customer_documents_all")
      : selectedTitle || t("customer_documents_uncategorized");

  // Below xl the tree does not fit beside the list (the card is only about two
  // thirds of the page wide): it opens in a drawer from a "Kategorija: …"
  // button instead, and picking a branch closes it.
  const [isTreeDrawerOpen, setIsTreeDrawerOpen] = useState(false);
  const selectBranchFromDrawer = useCallback(
    (id) => {
      selectCategory(id);
      setIsTreeDrawerOpen(false);
    },
    [selectCategory]
  );

  // Skeleton only until the customer has a list to show. A refresh after a
  // row action keeps the rows (scroll position, open "Susiję" lists) until
  // the new list replaces them. useCustomerFiles hands over no files while
  // the list is not this customer's, so an empty list here covers that too.
  const isLoading = isLoadingCustomerFiles || isLoadingFilteredFiles;
  const showSkeleton = isLoading && filteredFiles.length === 0;
  // A new search or category is loading while the previous rows are still
  // shown: dim them so they are not taken for the new result.
  const isRefreshing = isLoading && filteredFiles.length > 0;
  // Nothing to browse by: no document carries a category (or none loaded yet).
  const hasTree = tree.nodes.length > 0;

  // Desk #337: tick documents here and add them (with their child documents —
  // the server does that, see addFileThunk) to the existing document cart,
  // downloaded from there as today. Selection is intentionally NOT reset when
  // the tree branch changes, so switching categories does not lose ticks.
  const [selectedFileIds, setSelectedFileIds] = useState({});
  const { addFiles, isAdding } = useAddFilesToCart({ source: "document" });

  // The full set of ids a checkbox can currently exist for — filteredFiles
  // already carries the caller's search/category filters, but NOT the tree
  // branch narrowing below (visibleFiles), so a tick survives switching
  // branches. We deliberately do NOT prune `selectedFileIds` when this set
  // changes (e.g. while typing a search) — that would silently drop ticks.
  // Instead, anything selected that has fallen out of it (deleted/moved, or
  // just hidden by the current search — we can't tell which, and it doesn't
  // matter) is excluded from the count/bar/add-action below via
  // `effectiveIds`, but stays in state so it counts again once visible.
  const selectableFilteredIds = useMemo(
    () =>
      new Set(
        filteredFiles
          .filter((file) => isSelectableRow(file, false))
          .map((file) => file.id)
      ),
    [filteredFiles]
  );
  const effectiveIds = useMemo(
    () =>
      Object.keys(selectedFileIds).filter((id) =>
        selectableFilteredIds.has(id)
      ),
    [selectedFileIds, selectableFilteredIds]
  );
  const selectedCount = effectiveIds.length;

  const handleToggleFileSelection = useCallback((file) => {
    setSelectedFileIds((prev) => {
      const next = { ...prev };
      if (next[file.id]) {
        delete next[file.id];
      } else {
        next[file.id] = true;
      }
      return next;
    });
  }, []);

  const handleClearSelection = useCallback(() => setSelectedFileIds({}), []);

  const handleAddSelectedToCart = useCallback(async () => {
    // Snapshot at click time — the add is sequential and can take a while,
    // during which the user may tick more files or hit Clear. Only remove
    // exactly the ids that were sent AND succeeded (added or already in
    // cart); a functional update over the CURRENT state (not this snapshot)
    // leaves any concurrent change the user made in between untouched.
    const sent = effectiveIds;
    if (sent.length === 0) return;
    const summary = await addFiles(sent);
    const remaining = new Set(summary.remainingIds);
    setSelectedFileIds((prev) => {
      const next = { ...prev };
      for (const id of sent) {
        if (!remaining.has(id)) delete next[id];
      }
      return next;
    });
  }, [effectiveIds, addFiles]);

  // The files currently on screen (tree branch or the flat fallback) that get a checkbox.
  const currentFiles = hasTree ? visibleFiles : filteredFiles;
  const currentFileIds = useMemo(
    () =>
      currentFiles
        .filter((file) => isSelectableRow(file, false))
        .map((file) => file.id),
    [currentFiles]
  );
  const allCurrentSelected =
    currentFileIds.length > 0 &&
    currentFileIds.every((id) => selectedFileIds[id]);

  const handleToggleSelectAll = useCallback(() => {
    setSelectedFileIds((prev) => {
      const next = { ...prev };
      if (allCurrentSelected) {
        currentFileIds.forEach((id) => delete next[id]);
      } else {
        currentFileIds.forEach((id) => {
          next[id] = true;
        });
      }
      return next;
    });
  }, [allCurrentSelected, currentFileIds]);

  const selectAllToggle = currentFileIds.length > 0 && (
    <div className="flex items-center justify-end py-1 text-sm">
      <button
        type="button"
        className="text-indigo-600 hover:underline"
        onClick={handleToggleSelectAll}
      >
        {allCurrentSelected
          ? t("customer_documents_unselect_all")
          : t("customer_documents_select_all")}
      </button>
    </div>
  );

  // On a phone the bar moves under the list and sticks to the bottom of the
  // screen, above the phone's bottom bar, so "add to cart" stays in reach
  // while ticking further down. The page already has the phone bar's height as
  // bottom padding and a sticky inset counts from inside it, so the inset is
  // only the 0.5rem gap, not the bar height on top of it.
  const selectionBar = selectedCount > 0 && (
    <div className="mb-2 flex items-center gap-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm max-md:sticky max-md:bottom-2 max-md:z-10 max-md:order-last max-md:mb-0 max-md:mt-2 max-md:shadow-md">
      <span className="font-medium text-gray-900">
        {t("document_cart_selected_count", { count: selectedCount })}
      </span>
      <button
        type="button"
        className="text-indigo-600 hover:underline max-md:min-h-11"
        onClick={handleClearSelection}
      >
        {t("document_cart_clear_selection")}
      </button>
      <div className="flex-grow" />
      <button
        type="button"
        className="rounded bg-indigo-600 px-3 py-1.5 text-white hover:bg-indigo-700 disabled:opacity-50 max-md:min-h-11"
        onClick={handleAddSelectedToCart}
        disabled={isAdding}
      >
        {t("document_cart_add_selected")}
      </button>
    </div>
  );

  const fileList = (files, emptyText) => (
    <div
      className={isRefreshing ? "flex flex-col opacity-60 transition-opacity" : "flex flex-col transition-opacity"}
      aria-busy={isRefreshing}
    >
      {selectionBar}
      {selectAllToggle}
      {files.length > 0 ? (
        <Files
          files={files}
          customerId={customerId}
          onChanged={handleOnFileChange}
          selectedFileIds={selectedFileIds}
          onToggleFileSelection={handleToggleFileSelection}
        />
      ) : (
        <p className="py-6 text-center text-sm text-gray-500">{emptyText}</p>
      )}
    </div>
  );

  return (
    <Card roundedAbove="sm">
      <BlockStack gap="400">
        <div className="flex">
          <Text as="h2" variant="headingSm">
            {t("files")}
          </Text>
          <div className="flex-grow"></div>
          <ButtonGroup>
            <UploadFile
              onChange={handleOnFileChange}
              subject="customer"
              subjectId={customerId}
            />
          </ButtonGroup>
        </div>
        <TextField
          label={t("search_files") || "Search files"}
          value={searchQuery}
          onChange={handleSearchChange}
          autoComplete="off"
          placeholder={t("search_files") || "Search files..."}
          clearButton
          onClearButtonClick={() => handleSearchChange("")}
        />
      </BlockStack>
      {companyId && (
        <FavoriteCategoryFilter
          companyId={companyId}
          subject="customer"
          selectedCategories={selectedCategoryFilters}
          onCategoriesChange={handleCategoryFilterChange}
          isLoading={isLoadingCustomerFiles}
        />
      )}
      {showSkeleton ? (
        <FilesListSkeleton />
      ) : hasTree ? (
        <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
          <div className="hidden xl:block xl:border-r xl:border-gray-200 xl:pr-3">
            <CustomerDocumentTree
              tree={tree}
              selectedId={selectedId}
              onSelect={selectCategory}
            />
          </div>
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-2 xl:hidden">
              <button
                type="button"
                className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 max-xl:min-h-11"
                aria-haspopup="dialog"
                onClick={() => setIsTreeDrawerOpen(true)}
              >
                <Folder className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
                <span className="shrink-0 text-gray-500">{t("category")}:</span>
                <span className="truncate font-medium">{selectedBranchTitle}</span>
                <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
              </button>
              <span className="shrink-0 text-xs text-gray-500">{visibleFiles.length}</span>
            </div>
            {selectedId !== ALL_NODE_ID && (
              <div className="hidden items-center gap-2 border-b border-gray-200 pb-2 text-sm xl:flex">
                <span className="truncate font-medium text-gray-900">
                  {selectedBranchTitle}
                </span>
                <span className="text-gray-400">{visibleFiles.length}</span>
                <button
                  type="button"
                  className="ml-auto shrink-0 text-indigo-600 hover:underline"
                  onClick={() => selectCategory(ALL_NODE_ID)}
                >
                  {t("customer_documents_show_all")}
                </button>
              </div>
            )}
            {fileList(visibleFiles, t("files_empty_category"))}
          </div>
          <Dialog
            open={isTreeDrawerOpen}
            onClose={() => setIsTreeDrawerOpen(false)}
            className="relative z-[1000] xl:hidden"
          >
            <DialogBackdrop className="fixed inset-0 bg-zinc-950/40" />
            <DialogPanel className="fixed inset-y-0 left-0 flex w-[min(22rem,85vw)] flex-col bg-white shadow-xl">
              <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2.5">
                <DialogTitle className="flex-1 text-sm font-semibold text-gray-900">
                  {t("category")}
                </DialogTitle>
                <button
                  type="button"
                  className="rounded-md p-3 text-gray-500 hover:bg-gray-100"
                  aria-label={t("close")}
                  onClick={() => setIsTreeDrawerOpen(false)}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-2">
                <CustomerDocumentTree
                  tree={tree}
                  selectedId={selectedId}
                  onSelect={selectBranchFromDrawer}
                />
              </div>
            </DialogPanel>
          </Dialog>
        </div>
      ) : (
        fileList(filteredFiles, t("files_empty"))
      )}
    </Card>
  );
};

export default CustomerDocumentsSection;
